import { useTranslation } from 'react-i18next';
import { Heading } from 'shared/components/Heading';
import { useScrollReveal } from 'shared/hooks/useScrollReveal';
import { renderTitle } from 'shared/utils/renderTitle';
import type { Episode } from 'shared/data/episodes';
import { getEpisodeLabel } from 'shared/utils/getEpisodeLabel';

type HeroSectionProps = {
  episode: Episode;
  number: string;
};

export function HeroSection({ episode, number }: HeroSectionProps) {
  const { t } = useTranslation('episodeDetail');
  const badges = useScrollReveal({ animation: 'fade', delay: 0 });
  const heading = useScrollReveal({ animation: 'float-up', delay: 120 });
  const description = useScrollReveal({ animation: 'float-up', delay: 240 });
  const image = useScrollReveal({ animation: 'fade-image' });

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
      <div className="lg:col-span-7">
        <div ref={badges.ref} style={badges.style} className="flex items-center gap-4 mb-6">
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-tertiary font-bold bg-tertiary/10 px-3 py-1 rounded-full">
            {getEpisodeLabel(number)}
          </span>
          {episode.publishedDate && <span className="font-sans text-sm text-on-surface-variant italic">
            {t('published', { date: episode.publishedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) })}
          </span>}
        </div>

        <Heading
          variant="display"
          innerRef={heading.ref}
          style={heading.style}
          className="leading-[1.1] mb-8"
        >
          {renderTitle(episode.title, episode.titleHighlight)}
        </Heading>

        <p
          ref={description.ref}
          style={description.style}
          className="text-on-surface-variant text-xl leading-relaxed max-w-2xl font-light"
        >
          {episode.description}
        </p>
      </div>

      <div ref={image.ref} style={image.style} className="lg:col-span-5 relative pr-12">
        <img
          src={episode.image?.src}
          alt={episode.image?.alt}
          className="aspect-[4/5] rounded-xl overflow-hidden shadow-2xl rotate-3 translate-x-4 object-cover"
        />
        {/* Drink image */}
        {/* <div className="absolute -bottom-8 -left-8 aspect-square w-48 bg-surface-bright p-4 rounded-xl shadow-2xl -rotate-6 hidden lg:block" /> */}
      </div>
    </section>
  );
}
